<template>
  <v-container class="my-0 ml-0 mr-n2 pa-0" fluid>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-2">
        <p>Agent configs. All fields are required. Agent prompt can be autofilled based on the name and description.</p>
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" density="compact" class="ma-0 pa-2 d-flex align-center">
        <Button buttonClass="ml-1" icon="mdi-plus" v-if="count > 0" :click="openCreateAgentOverlay" size="x-large" tooltip="New Agent" variant="outlined" density="default" color="secondary"></Button>
        <v-btn-toggle v-model="sortOrder" mandatory class="ml-4" density="compact">
          <v-btn icon="mdi-sort-variant-remove" value="default" size="small" title="Default Order"></v-btn>
          <v-btn icon="mdi-sort-alphabetical-ascending" value="asc" size="small" title="Sort Ascending"></v-btn>
          <v-btn icon="mdi-sort-alphabetical-descending" value="desc" size="small" title="Sort Descending"></v-btn>
        </v-btn-toggle>
        <div class="filter ml-4">
          <v-combobox v-model="search" @update:modelValue="filterAgentNames" :items="configList" :return-object="false"
            item-title="name" item-value="name" clearable variant="outlined" density="compact" label="Filter Agent" hide-details>
            <template v-slot:append>
              <span>{{ count }}</span>
            </template>
          </v-combobox>
        </div>
        <div class="agents-toolbar-center d-flex justify-center">
          <v-btn
            color="primary"
            variant="outlined"
            prepend-icon="mdi-video-plus-outline"
            @click="openWorkflowVideoToAgent"
          >
            Video To Agent
          </v-btn>
        </div>
        <v-tooltip text="Refresh Agents" location="top">
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" color="primary" variant="text" icon :loading="refreshingAgents" @click="refreshAgentsView(true)">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0" v-if="filteredAgentNames != null">
      <v-col v-for="agentName in filteredAgentNames" :key="agentName" :cols="isDesktop ? 4 : 12" class="ma-0 pa-2" density="compact">
        <AgentSummaryCard :agent="agentConfigs[agentName]" :variant="isDesktop ? 'desktop' : 'compact'" @edit="openEditAgentOverlay" />
      </v-col>
      <v-col v-if="count === 0" :cols="isDesktop ? 4 : 12" class="ma-0 pa-2">
        <v-card
          @click="openCreateAgentOverlay"
          class="ma-0 pa-2 card add-card"
          color="surface"
          density="compact"
          variant="elevated"
          elevation="4"
          min-height="350"
        >
          <p>New agent</p>
          <v-icon class="add-icon" icon="mdi-plus"></v-icon>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="sharedAgentsSorted.length > 0" density="compact" class="ma-0 pa-0 mt-6">
      <v-col cols="12" class="ma-0 pa-2">
        <h3 class="text-h6">Shared Agents</h3>
        <p class="text-body-2 text-medium-emphasis">Agents shared with you.</p>
      </v-col>
      <v-col v-for="sharedAgent in sharedAgentsSorted" :key="sharedAgent.agentId" :cols="isDesktop ? 4 : 12" class="ma-0 pa-2" density="compact">
        <SharedAgentCard :agent="sharedAgent" @edit="openEditSharedAgentOverlay" />
      </v-col>
    </v-row>
    <ConfigCardOverlay
      v-model="showCreateAgentOverlay"
      title="New Agent"
      @close="closeCreateAgentOverlay"
    >
      <Agent
        isOverlay
        :showTips="showTips"
        @remove-card="closeCreateAgentOverlay"
        @saved="handleAgentSaved"
      />
    </ConfigCardOverlay>
    <ConfigCardOverlay
      v-model="showEditAgentOverlay"
      title="Edit Agent"
      @close="closeEditAgentOverlay"
    >
      <Agent
        v-if="editingAgentId"
        :id="editingAgentId"
        isOverlay
        :showTips="showTips"
        :startExpanded="true"
        @remove-card="closeEditAgentOverlay"
        @saved="handleAgentSaved"
      />
    </ConfigCardOverlay>
    <ConfigCardOverlay
      v-model="showEditSharedAgentOverlay"
      title="Edit Shared Agent"
      @close="closeEditSharedAgentOverlay"
    >
      <Agent
        v-if="editingSharedAgent"
        :sharedAgent="editingSharedAgent"
        isOverlay
        :showTips="showTips"
        :startExpanded="true"
        @remove-card="closeEditSharedAgentOverlay"
        @saved="handleSharedAgentSaved"
      />
    </ConfigCardOverlay>
  </v-container>
</template>

<script lang="ts">
import { Options, mixins } from 'vue-class-component'
import { mapGetters } from 'vuex'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import Agent from '@/components/cards/AgentCard.vue'
import AgentSummaryCard from '@/components/cards/AgentSummaryCard.vue'
import SharedAgentCard from '@/components/cards/SharedAgentCard.vue'
import ConfigCardOverlay from '@/components/overlays/ConfigCardOverlay.vue'
import Button from '@/components/sub/Button.vue'
import type { ConfigRefreshTarget } from '@/store/global'
import { SharedAgentInfo, UserAgentConfig, UserAgentConfigs } from '@/types/agents'

@Options({
  props: {
    showTips: Boolean
  },
  components: {
    Agent,
    AgentSummaryCard,
    SharedAgentCard,
    ConfigCardOverlay,
    Button
  },
  computed: {
    ...mapGetters(['agentNames', 'agentConfigs', 'agentConfigsList', 'sharedAgents', 'selectedTab', 'configRefreshToken'])
  },
  watch: {
    agentConfigs: {
      handler() {
        this.filterAgentNames()
      },
      deep: true
    },
    sortOrder() {
      this.filterAgentNames()
    },
    selectedTab(value: string) {
      if (value === 'agents') {
        void this.maybeRefreshAgentsView()
      }
    },
    agentsRefreshToken() {
      void this.maybeRefreshAgentsView()
    }
  }
})
export default class Agents extends mixins(AnalyticsMixin) {
  agentNames!: string[]
  agentConfigs!: UserAgentConfigs
  agentConfigsList!: UserAgentConfig[]
  sharedAgents!: SharedAgentInfo[]
  showTips!: boolean
  selectedTab!: string
  configRefreshToken!: (target: ConfigRefreshTarget) => number
  filteredAgentNames: string[] = []
  search: string | null = null
  sortOrder: 'default' | 'asc' | 'desc' = 'default'
  showCreateAgentOverlay = false
  showEditAgentOverlay = false
  editingAgentId: string | null = null
  expandedAgentName: string | null = null
  refreshingAgents = false
  lastHandledAgentsRefreshToken = -1
  showEditSharedAgentOverlay = false
  editingSharedAgent: SharedAgentInfo | null = null

  get configList() {
    return (this.agentConfigsList ?? []).map(a => a.config)
  }

  get agentsRefreshToken(): number {
    return this.configRefreshToken('agents')
  }

  get count(): number {
    return this.filteredAgentNames.length
  }

  get isDesktop(): boolean {
    return (this as any).$vuetify.display.mdAndUp
  }

  get sharedAgentsSorted() {
    return [...(this.sharedAgents ?? [])].sort((a, b) => a.agentName.localeCompare(b.agentName))
  }

  async mounted() {
    this.trackComponentView('agents_dashboard');
    this.filteredAgentNames = this.agentNames
    if (this.selectedTab === 'agents') {
      await this.refreshAgentsView(true)
    }
  }

  filterAgentNames() {
    const { search } = this
    console.log(search)
    if (search == null || search === '') {
      this.filteredAgentNames = [...this.agentNames]
    } else {
      this.filteredAgentNames = this.configList
        .filter((agent) => agent.name.includes(search) || agent.description.includes(search))
        .map((agent) => agent.name)
    }

    // Sort the filtered list
    if (this.sortOrder !== 'default') {
      this.filteredAgentNames.sort((nameA, nameB) => {
        const agentA = this.agentConfigs[nameA];
        const agentB = this.agentConfigs[nameB];
        if (!agentA || !agentB) return 0;

        const configA = agentA.config.name.toLowerCase();
        const configB = agentB.config.name.toLowerCase();

        return this.sortOrder === 'asc' ? configA.localeCompare(configB) : configB.localeCompare(configA);
      });
    }
    
    console.log(`filtered to ${this.count} agents`)
  }

  openCreateAgentOverlay() {
    this.trackButtonClick('create_agent_start', 'click');
    this.showCreateAgentOverlay = true
  }

  openWorkflowVideoToAgent() {
    this.$store.dispatch('toggleVideoToAgentDialog', true)
  }

  closeCreateAgentOverlay() {
    this.showCreateAgentOverlay = false
  }

  openEditAgentOverlay(agentName: string) {
    this.editingAgentId = agentName
    this.showEditAgentOverlay = true
  }

  closeEditAgentOverlay() {
    this.showEditAgentOverlay = false
    this.editingAgentId = null
  }

  openEditSharedAgentOverlay(sharedAgent: SharedAgentInfo) {
    this.editingSharedAgent = sharedAgent
    this.showEditSharedAgentOverlay = true
  }

  closeEditSharedAgentOverlay() {
    this.showEditSharedAgentOverlay = false
    this.editingSharedAgent = null
  }

  handleSharedAgentSaved() {
    this.closeEditSharedAgentOverlay()
    void this.$store.dispatch('fetchSharedAgents')
  }

  async refreshAgentsView(force = false) {
    if (this.refreshingAgents) return
    this.refreshingAgents = true
    try {
      await Promise.all([
        this.$store.dispatch('fetchModelConfigs'),
        this.$store.dispatch('fetchPublishedAgents'),
        this.$store.dispatch('fetchSharedAgents')
      ])
      this.filterAgentNames()
      this.lastHandledAgentsRefreshToken = this.agentsRefreshToken
    } finally {
      this.refreshingAgents = false
    }
  }

  async maybeRefreshAgentsView(force = false) {
    if (this.selectedTab !== 'agents' && !force) return
    const needsRefresh = force || this.lastHandledAgentsRefreshToken !== this.agentsRefreshToken
    if (needsRefresh) {
      await this.refreshAgentsView(force)
    }
  }

  handleAgentSaved(payload?: { name?: string }) {
    if (payload?.name) {
      this.expandedAgentName = payload.name
      if (this.search) {
        const search = this.search.toLowerCase()
        const matches = payload.name.toLowerCase().includes(search)
          || (this.agentConfigs[payload.name]?.config.description.toLowerCase().includes(search) ?? false)
        if (!matches) {
          this.$store.dispatch('updateSnackbar', {
            text: `Saved agent ${payload.name}. The current filter hides it.`,
            timeout: 4000
          })
        }
      }
    }
    this.lastHandledAgentsRefreshToken = this.agentsRefreshToken
    this.showCreateAgentOverlay = false
    this.showEditAgentOverlay = false
  }
}
</script>
<style scoped lang="scss">
.add-card {
  border: 1px solid rgba(255, 255, 255, 0.3);
  height: 350px;
  text-align: center;
  cursor: pointer;

  p {
    margin-top: 130px;
  }
}

.add-card-small {
  border: 1px solid rgba(255, 255, 255, 0.3);
  text-align: center;
  cursor: pointer;
}
.filter {
  width: 350px;
  max-width: 500px;
}

.agents-toolbar-center {
  flex: 1 1 auto;
}
</style>
